import React, { useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useFamilyStore } from '@/hooks/useStores';
import ChevronDown from '@/icons/ChevronDown';
import History from '@/icons/History';
import i18n from '@/i18n/i18n';
import { calendarYearFromStoredDate } from '@/utils/dateFormatting';
import { getTreeRecentAnchors } from '@/utils/treeRecentAnchors';

interface TreeRecentAnchorsProps {
  familyId: string;
  currentAnchorId: string | null;
  onPick: (familyMemberId: string) => void;
}

function nameInitials(name: string): string {
  return (
    name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((s) => s[0])
      .join('')
      .toUpperCase() || '?'
  );
}

function formatPersonDates(person: { birthDate?: string | null; deathDate?: string | null }): string {
  if (!person) return '';
  const birthYear = calendarYearFromStoredDate(person.birthDate);
  const deathYear = calendarYearFromStoredDate(person.deathDate);
  if (birthYear && deathYear) return i18n.t('common:genealogy.yearRange', { birth: birthYear, death: deathYear });
  if (birthYear) return i18n.t('common:genealogy.bornYear', { year: birthYear });
  if (deathYear) return i18n.t('common:genealogy.diedYear', { year: deathYear });
  return '';
}

const TreeRecentAnchors: React.FC<TreeRecentAnchorsProps> = observer(({ familyId, currentAnchorId, onPick }) => {
  const { t } = useTranslation('pedigree');
  const { t: tCommon } = useTranslation('common');
  const familyStore = useFamilyStore();
  const [open, setOpen] = useState(false);
  const [recents, setRecents] = useState<string[]>(() => getTreeRecentAnchors(familyId));
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setRecents(getTreeRecentAnchors(familyId));
  }, [familyId, currentAnchorId]);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const peekId = recents[0];
  const peekPerson = peekId ? familyStore.getPerson(peekId) : null;
  const peekFirstName = peekPerson?.fullName?.split(/\s+/)[0];

  return (
    <div className="tree-recents" ref={ref}>
      <button
        type="button"
        className="tree-recents__btn"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-haspopup="menu"
        title={t('recentAnchors.jumpTitle')}
      >
        <History width={13} height={13} />
        {peekFirstName ? (
          <>
            {t('recentAnchors.recentPrefix')} <strong>{peekFirstName}</strong>
          </>
        ) : (
          <>{t('recentAnchors.title')}</>
        )}
        <ChevronDown width={10} height={10} aria-hidden />
      </button>
      {open && (
        <div className="tree-recents__menu" role="menu">
          <div className="tree-recents__heading">{t('recentAnchors.title')}</div>
          {recents.length === 0 ? (
            <p className="tree-recents__empty">
              {t('recentAnchors.empty')}
              <br />
              {t('recentAnchors.emptyHint')}
            </p>
          ) : (
            recents.map((id) => {
              const person = familyStore.getPerson(id);
              if (!person) return null;
              return (
                <button
                  key={id}
                  type="button"
                  className="tree-recents__item"
                  role="menuitem"
                  onClick={() => {
                    onPick(id);
                    setOpen(false);
                  }}
                >
                  <span className="tree-recents__av" aria-hidden>
                    {nameInitials(person.fullName)}
                  </span>
                  <span className="tree-recents__name">{person.fullName}</span>
                  <span className="tree-recents__dates">{formatPersonDates(person) || tCommon('emptyDefault')}</span>
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
});

export default TreeRecentAnchors;
